import api from './api'
import type { FileMetadata, UploadResponse, AnalyticsResponse, EncryptionMetadata } from '@/types'

export const fileService = {
  async upload(
    files: File[],
    options?: {
      password?: string
      maxDownloads?: number
      onProgress?: (percent: number) => void
      signal?: AbortSignal
      isEncrypted?: boolean
      encryptionMetadata?: EncryptionMetadata[]
    }
  ): Promise<UploadResponse> {
    const formData = new FormData()

    files.forEach((file) => {
      formData.append('files', file)
    })

    if (options?.password) {
      formData.append('password', options.password)
    }

    if (options?.maxDownloads) {
      formData.append('max_downloads', options.maxDownloads.toString())
    }

    if (options?.isEncrypted) {
      formData.append('is_encrypted', 'true')
      if (options.encryptionMetadata && options.encryptionMetadata.length > 0) {
        formData.append('encryption_metadata', JSON.stringify(options.encryptionMetadata))
      }
    }

    const response = await api.post<UploadResponse>('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal: options?.signal,
      onUploadProgress: (event) => {
        if (event.total && options?.onProgress) {
          const percent = Math.round((event.loaded * 100) / event.total)
          options.onProgress(percent)
        }
      },
    })

    return response.data
  },

  async getMetadata(fileId: string): Promise<FileMetadata> {
    const response = await api.get<FileMetadata>(`/files/${fileId}`)
    return response.data
  },

  async download(fileId: string, password?: string): Promise<void> {
    const blob = await this.downloadBlob(fileId, password)
    const filename = await this.getFilenameForDownload(fileId)
    triggerBlobDownload(blob, filename)
  },

  async downloadBlob(fileId: string, password?: string): Promise<Blob> {
    const headers: Record<string, string> = {}
    if (password) headers['X-File-Password'] = password

    // Request as blob so we handle both JSON (signed URL) and binary (streaming) responses
    const response = await api.get(`/download/${fileId}`, {
      headers,
      responseType: 'blob',
    })

    const blob = response.data as Blob

    // If the backend returned JSON (signed URL from R2/GCS), parse and fetch from the URL
    if (blob.type === 'application/json' || blob.type.startsWith('application/json')) {
      const text = await blob.text()
      const json = JSON.parse(text)
      if (json.url) {
        const storageResponse = await fetch(json.url)
        if (!storageResponse.ok) {
          throw new Error('Failed to download file from storage')
        }
        return storageResponse.blob()
      }
    }

    // Otherwise the response IS the file (local storage streaming)
    return blob
  },

  async getFilenameForDownload(fileId: string): Promise<string> {
    try {
      const meta = await this.getMetadata(fileId)
      return meta.original_name || 'download'
    } catch {
      return 'download'
    }
  },

  async downloadBundle(fileIds: string[], password?: string): Promise<void> {
    const params = new URLSearchParams()
    params.set('ids', fileIds.join(','))

    const headers: Record<string, string> = {}
    if (password) headers['X-File-Password'] = password

    const response = await api.get(`/bundle/download?${params.toString()}`, {
      responseType: 'blob',
      headers,
    })

    const blob = new Blob([response.data], { type: 'application/zip' })
    triggerBlobDownload(blob, 'vaultlynk-files.zip')
  },

  async deleteFile(fileId: string): Promise<void> {
    await api.delete(`/files/${fileId}`)
  },

  async getUserFiles(): Promise<FileMetadata[]> {
    const response = await api.get<FileMetadata[]>('/users/files')
    return response.data
  },

  async getAnalytics(): Promise<AnalyticsResponse> {
    const response = await api.get<AnalyticsResponse>('/users/analytics')
    return response.data
  },

  async getNotificationPreferences(): Promise<{ notify_on_download: boolean }> {
    const response = await api.get<{ notify_on_download: boolean }>('/users/notifications/preferences')
    return response.data
  },

  async setNotificationPreferences(notifyOnDownload: boolean): Promise<void> {
    await api.put('/users/notifications/preferences', { notify_on_download: notifyOnDownload })
  },
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(objectUrl)
}
